import { sentryCaptureError, sentryCaptureWarning } from "@utils/sentry-utils";

/** Base used for hexadecimal conversion */
const HEX_BASE = 16;

/** Minimum length for each hex segment */
const HEX_PAD_LENGTH = 2;

/** Padding character for hex values */
const HEX_PAD_CHAR = "0";

/** Forbidden, e.g. an expired signed URL */
const HTTP_FORBIDDEN = 403;

/** The resource does not exist */
const HTTP_NOT_FOUND = 404;

/** The resource existed but is permanently gone */
const HTTP_GONE = 410;

/** Statuses meaning the blob is simply not there, which is a normal backend response */
const EXPECTED_MISSING_STATUSES = [HTTP_FORBIDDEN, HTTP_NOT_FOUND, HTTP_GONE];

/** Supported context types for image caching */

/**
 * Thrown when the requested resource does not exist or is not an image.
 * This is an expected outcome, since the backend returns thumbnail URLs without
 * checking that the blob behind them still exists.
 */
export class ImageNotAvailableError extends Error {
  constructor(src, status) {
    super(`Image not available (status ${status}): ${src}`);
    this.name = "ImageNotAvailableError";
  }
}

/** Issues already reported in this session */
const reportedIssues = new Set();

/**
 * Reports an image problem at most once per session.
 * Every failure here can repeat for each thumbnail on screen, which would flood Sentry.
 */
function reportImageIssueOnce(key, title, data) {
  if (reportedIssues.has(key)) {
    return;
  }
  reportedIssues.add(key);
  sentryCaptureWarning({
    title,
    warningType: "image",
    data
  });
}

/**
 * Whether the blob can be displayed as an image.
 * An empty type is accepted because some storages do not set a content type.
 */
function isImageBlob(blob) {
  return blob.type === "" || blob.type.startsWith("image/");
}

/**
 * Manages image caching, compression, and retrieval.
 */
class ImageManager {
  /** Name of the cache storage holding the images of a company */
  getCacheName(companyId) {
    return `image-cache-${companyId}`;
  }
  /**
   * Builds a unique cache key for an image based on its source and context.
   *
   * @throws Will throw an error if the hash generation fails.
   */
  async buildCacheKey(src, contextType, contextId) {
    try {
      const hash = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(src));
      const hashArray = Array.from(new Uint8Array(hash));
      const hashHex = hashArray.map(b => b.toString(HEX_BASE).padStart(HEX_PAD_LENGTH, HEX_PAD_CHAR)).join("");
      return `https://${contextType}-${contextId}/${hashHex}`;
    } catch (error) {
      sentryCaptureError({
        error,
        title: "Error generating cache key"
      });
      throw error;
    }
  }

  /**
   * Retrieves an image from cache or fetches it from the network.
   * An object containing the response and a flag indicating if compression should be skipped.
   */
  async getCachedOrFetch(cacheKey, src, companyId) {
    if (!("caches" in globalThis)) {
      return {
        response: await fetch(src),
        shouldSkipCompression: true
      };
    }
    if (src.startsWith("/src/assets")) {
      return {
        response: await fetch(src),
        shouldSkipCompression: true
      };
    }
    const cache = await caches.open(this.getCacheName(companyId));
    const cached = await cache.match(cacheKey);
    if (cached) {
      return {
        response: cached,
        shouldSkipCompression: true
      };
    }
    return {
      response: await fetch(src),
      shouldSkipCompression: false
    };
  }

  /** Removes a cache entry, so a corrupted image can be fetched again later */
  async deleteCacheEntry(cacheKey, companyId) {
    if (!("caches" in globalThis)) {
      return;
    }
    try {
      const cache = await caches.open(this.getCacheName(companyId));
      await cache.delete(cacheKey);
    } catch (error) {
      reportImageIssueOnce("cache-delete", "Could not delete an image from the cache", {
        error: String(error)
      });
    }
  }

  /**
   * Compresses an image blob using a Web Worker.
   *
   * Falls back to the original blob if compression fails.
   */
  workerCompress(blob) {
    return new Promise(resolve => {
      try {
        const worker = new Worker(new URL("../workers/image-compress-worker.ts", import.meta.url), {
          type: "module"
        });
        worker.postMessage(blob);
        worker.onmessage = e => {
          const compressedBlob = e.data;
          if (compressedBlob.size === 0) {
            resolve(blob);
          } else {
            resolve(compressedBlob);
          }
        };
      } catch (error) {
        reportImageIssueOnce("worker-init", "Could not initialize the image compression worker", {
          error: String(error)
        });
        resolve(blob);
      }
    });
  }

  /** Removes old cache entries for a given context, keeping only the current key */
  async cleanOldCacheEntries(cache, contextType, contextId, currentCacheKey) {
    if (!("caches" in globalThis)) {
      return;
    }
    try {
      const keys = await cache.keys();
      const prefix = `https://${contextType}-${contextId}/`;
      await Promise.all(keys.map(async request => {
        if (request.url.startsWith(prefix) && request.url !== currentCacheKey) {
          await cache.delete(request);
        }
      }));
    } catch (err) {
      reportImageIssueOnce("cache-clean", "Could not clean old images from the cache", {
        error: String(err)
      });
    }
  }

  /**
   * Retrieves an image as a Blob, compresses it if necessary, and caches it.
   *
   * @throws ImageNotAvailableError if the resource does not exist or is not an image.
   */
  async getImageBlob(src, companyId, contextType, contextId) {
    const cacheKey = await this.buildCacheKey(src, contextType, contextId);
    const {
      response,
      shouldSkipCompression
    } = await this.getCachedOrFetch(cacheKey, src, companyId);

    // fetch() does not reject on 4xx/5xx, and a missing blob is answered with an XML error body
    if (!response.ok) {
      if (!EXPECTED_MISSING_STATUSES.includes(response.status)) {
        reportImageIssueOnce(`status-${response.status}`, "Image request failed with an unexpected status", {
          status: response.status,
          src
        });
      }
      throw new ImageNotAvailableError(src, response.status);
    }
    const originalBlob = await response.blob();
    if (originalBlob.size === 0 || !isImageBlob(originalBlob)) {
      // The entry can come from a previously cached error response
      await this.deleteCacheEntry(cacheKey, companyId);
      throw new ImageNotAvailableError(src, response.status);
    }
    if (shouldSkipCompression) {
      return originalBlob;
    }

    // Compress and store in cache
    const compressedBlob = await this.workerCompress(originalBlob);
    const cache = await caches.open(this.getCacheName(companyId));

    // Clean old entries for this context
    await this.cleanOldCacheEntries(cache, contextType, contextId, cacheKey);
    try {
      await cache.put(cacheKey, new Response(compressedBlob));
    } catch (error) {
      reportImageIssueOnce("cache-put", "Could not store an image in the cache", {
        error: String(error)
      });
    }
    return compressedBlob;
  }
}
export const imageManager = new ImageManager();